import React from "react";
import { Link } from "react-router-dom";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import TrustCues from "../components/TrustCues";
import { Button } from "../components/ui/button";
import { useSEO } from "../lib/useSEO";
import { PLAINTIFF_STEPS, VALUE_PROPS, CASE_TYPES } from "../data/content";
import { CASE_TYPE_TO_SLUG } from "../data/accidents";
import { CheckCircle2, ArrowRight, ShieldCheck } from "lucide-react";

export default function HowItWorks() {
  useSEO(
    // GSC-optimized: currently pos 1.64 with 0% CTR. Numeric hooks in title +
    // process visualization in meta + non-recourse risk-reversal.
    "How Pre-Settlement Funding Works: 3 Steps, 24-Hour Approval",
    "How pre-settlement funding actually works: apply in 15 minutes, attorney verifies your case, funds arrive in 24 hours. No credit check. Non-recourse — owe nothing if you lose.",
    { canonical: "https://instabridgefunding.com/how-it-works" }
  );

  return (
    <div className="min-h-screen bg-white" data-testid="howitworks-page">
      <Navbar />

      <section className="pt-16 pb-12 lg:pt-24 lg:pb-16 bg-[#F9FAFB] border-b border-[#E5E7EB]">
        <div className="mx-auto max-w-5xl px-6 lg:px-10">
          <div className="uppercase-label mb-5">For plaintiffs</div>
          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl tracking-tight text-[#0A2540] font-light leading-[1.05]">
            How <span className="font-semibold">pre-settlement funding</span> works.
          </h1>
          <p className="mt-6 text-lg text-[#6B7280] leading-relaxed max-w-3xl">
            A plain-English walkthrough of how you get cash advanced against your future settlement — with no credit check, no upfront cost, and no risk if you lose.
          </p>
        </div>
      </section>

      {/* Steps */}
      <section className="py-20 lg:py-24">
        <div className="mx-auto max-w-5xl px-6 lg:px-10 space-y-6">
          {PLAINTIFF_STEPS.map((s, i) => (
            <div key={s.n} className="bg-white border border-[#E5E7EB] rounded-2xl p-8 lg:p-10 grid sm:grid-cols-12 gap-6 items-start">
              <div className="sm:col-span-2 font-display text-5xl font-light text-[#0066FF] tabular-nums">{s.n}</div>
              <div className="sm:col-span-10">
                <h2 className="font-display text-2xl lg:text-3xl font-semibold text-[#0A2540]">{s.t}</h2>
                <p className="mt-3 text-[#6B7280] leading-relaxed text-base lg:text-lg">{s.d}</p>
                {i === 0 && (
                  <ul className="mt-5 space-y-2">
                    {["Takes about 4 minutes", "No credit check, no SSN required", "Free and no obligation"].map((x) => (
                      <li key={x} className="flex items-start gap-2 text-sm text-[#374151]">
                        <CheckCircle2 className="w-4 h-4 text-[#0066FF] flex-shrink-0 mt-0.5" />{x}
                      </li>
                    ))}
                  </ul>
                )}
                {i === 1 && (
                  <ul className="mt-5 space-y-2">
                    {["Secure tokenized link sent directly to your lawyer", "No account creation required for them", "Most attorneys verify in under 30 minutes"].map((x) => (
                      <li key={x} className="flex items-start gap-2 text-sm text-[#374151]">
                        <CheckCircle2 className="w-4 h-4 text-[#0066FF] flex-shrink-0 mt-0.5" />{x}
                      </li>
                    ))}
                  </ul>
                )}
                {i === 2 && (
                  <ul className="mt-5 space-y-2">
                    {["Funds wired or direct-deposited", "No payment due until your case settles", "Zero owed if you lose your case"].map((x) => (
                      <li key={x} className="flex items-start gap-2 text-sm text-[#374151]">
                        <CheckCircle2 className="w-4 h-4 text-[#0066FF] flex-shrink-0 mt-0.5" />{x}
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Cost example — placed before "what makes us different" so price/risk clarity lands first */}
      <section className="py-20 bg-[#F9FAFB] border-y border-[#E5E7EB]">
        <div className="mx-auto max-w-5xl px-6 lg:px-10">
          <div className="uppercase-label mb-5">Plain-english pricing</div>
          <h2 className="font-display text-3xl lg:text-4xl text-[#0A2540] font-light tracking-tight">What does it actually cost?</h2>
          <p className="mt-5 text-[#6B7280] leading-relaxed text-lg max-w-3xl">
            We charge a fee that accrues over time as a percentage of the advance. Rates are capped, disclosed up front, and never change. Here's a simple example:
          </p>
          <div className="mt-8 bg-[#0A2540] text-white rounded-2xl p-8 lg:p-10">
            <div className="grid sm:grid-cols-3 gap-6 items-stretch">
              <div>
                <div className="text-xs uppercase tracking-widest text-white/60">Advance amount</div>
                <div className="font-display text-3xl font-semibold mt-2">$10,000</div>
              </div>
              <div>
                <div className="text-xs uppercase tracking-widest text-white/60">Case settles in 12 months</div>
                <div className="font-display text-3xl font-semibold mt-2">~$13,000</div>
                <div className="text-xs text-white/60 mt-1">Approximate payoff to Instabridge</div>
              </div>
              {/* "If you lose" — visually elevated: highlighted background + accent border + larger payoff number */}
              <div className="-m-1 sm:-my-2 sm:-mr-2 p-5 sm:p-6 rounded-xl bg-[#0066FF]/15 border border-[#0066FF]/40 ring-1 ring-[#0066FF]/30" data-testid="howit-pricing-no-loss">
                <div className="text-xs uppercase tracking-widest text-[#6BA8FF]">If you lose</div>
                <div className="font-display text-4xl lg:text-5xl font-semibold mt-2 text-white">$0</div>
                <div className="text-xs text-white/70 mt-1">Non-recourse — you owe nothing, ever.</div>
              </div>
            </div>
            <p className="text-xs text-white/60 mt-6 italic">Example for illustration only. Actual rates vary by case type and term. Full pricing disclosed in your funding agreement before any advance is made.</p>
          </div>
        </div>
      </section>

      {/* Under the hood — how underwriting actually works */}
      <section className="py-20 bg-[#F9FAFB] border-t border-[#E5E7EB]">
        <div className="mx-auto max-w-6xl px-6 lg:px-10">
          <div className="uppercase-label mb-3">Under the hood</div>
          <h2 className="font-display text-3xl lg:text-4xl text-[#0A2540] font-light tracking-tight">How we actually underwrite a case.</h2>
          <p className="mt-4 text-[#374151] leading-relaxed max-w-3xl">
            Traditional lenders underwrite <em>you</em> — credit, income, debt-to-income ratio. Non-recourse legal funding is the opposite: we underwrite the <em>case</em>. There are three things we actually evaluate.
          </p>
          <div className="mt-10 grid md:grid-cols-3 gap-5">
            <div className="bg-white border border-[#E5E7EB] rounded-xl p-6" data-testid="howit-uw-liability">
              <div className="text-xs uppercase tracking-wider text-[#0066FF] font-semibold">01 · Liability</div>
              <h3 className="font-display text-lg font-semibold text-[#0A2540] mt-3">Who's at fault, how clearly.</h3>
              <p className="text-sm text-[#374151] mt-3 leading-relaxed">Clear liability (rear-end, red-light violation, safety-code violation) supports a larger advance. Contested liability, comparative negligence exposure, or defense-favorable video reduces the advance. In Ohio, being 51%+ at fault means no recovery (§ 2315.33), so comparative posture matters more here than in some states.</p>
            </div>
            <div className="bg-white border border-[#E5E7EB] rounded-xl p-6" data-testid="howit-uw-damages">
              <div className="text-xs uppercase tracking-wider text-[#0066FF] font-semibold">02 · Damages</div>
              <h3 className="font-display text-lg font-semibold text-[#0A2540] mt-3">What the case is worth.</h3>
              <p className="text-sm text-[#374151] mt-3 leading-relaxed">We use current medicals, wage-loss documentation, and comparable Ohio verdicts and settlements. We size to the <strong>conservative</strong> projected settlement, not the optimistic one. On a case with a $50K–$100K range, we underwrite to the $50K number and cap the advance at 8–15% of that — never 30-40% like some competitors.</p>
            </div>
            <div className="bg-white border border-[#E5E7EB] rounded-xl p-6" data-testid="howit-uw-timeline">
              <div className="text-xs uppercase tracking-wider text-[#0066FF] font-semibold">03 · Timeline</div>
              <h3 className="font-display text-lg font-semibold text-[#0A2540] mt-3">When it will resolve.</h3>
              <p className="text-sm text-[#374151] mt-3 leading-relaxed">Case posture (pre-suit demand, filed, mediation scheduled, trial set) tells us how long the fee will accrue. A case that's already at mediation prices very differently from a case that just filed complaint. Faster resolution = lower total cost to you.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Why we cap advances */}
      <section className="py-20 bg-white">
        <div className="mx-auto max-w-6xl px-6 lg:px-10 grid lg:grid-cols-2 gap-12 items-start">
          <div>
            <div className="uppercase-label mb-3">The cap</div>
            <h2 className="font-display text-3xl lg:text-4xl text-[#0A2540] font-light tracking-tight">Why we cap advances at 8–15% of your projected recovery.</h2>
            <div className="mt-6 space-y-4 text-[#374151] leading-relaxed">
              <p>Overfunding is the biggest ethical problem in the pre-settlement funding industry. When a company funds you at 30% or 40% of your projected recovery, then adds fees that compound over 24 months, plaintiffs regularly find themselves at settlement owing more than they'll receive.</p>
              <p>We cap at a fraction of the <strong>conservative</strong> projected recovery. If your case has an expected range of $50K–$100K, we underwrite to $50K and fund 8–15% of that. The result: even if the case settles at the low end of the range, you still walk away with the majority of the recovery after attorney fees, medical liens, and our repayment.</p>
              <p>The cap is why we can be transparent about pricing. A predatory advance size can never be repaired by a "competitive" rate.</p>
            </div>
          </div>
          <div className="bg-[#0A2540] text-white rounded-2xl p-8">
            <div className="uppercase-label !text-white/60 mb-3">Example</div>
            <div className="font-display text-2xl font-semibold">Ohio rear-end auto case</div>
            <dl className="mt-5 space-y-3 text-sm">
              <div className="flex justify-between border-b border-white/10 pb-3"><dt className="text-white/60">Conservative projected settlement</dt><dd className="font-medium">$50,000</dd></div>
              <div className="flex justify-between border-b border-white/10 pb-3"><dt className="text-white/60">Instabridge advance (10%)</dt><dd className="font-medium">$5,000</dd></div>
              <div className="flex justify-between border-b border-white/10 pb-3"><dt className="text-white/60">Payoff at 18 months</dt><dd className="font-medium">~$7,000</dd></div>
              <div className="flex justify-between border-b border-white/10 pb-3"><dt className="text-white/60">Attorney fee (typical 33%)</dt><dd className="font-medium">$16,500</dd></div>
              <div className="flex justify-between border-b border-white/10 pb-3"><dt className="text-white/60">Medical liens (estimated)</dt><dd className="font-medium">$8,000</dd></div>
              <div className="flex justify-between pt-1"><dt className="font-medium">Your net take-home</dt><dd className="font-semibold text-[#6BA8FF]">$18,500</dd></div>
            </dl>
            <p className="text-xs text-white/50 mt-4">Illustrative only. Every case is priced individually and your actual advance, fee schedule, and net take-home will be disclosed in your contract before you sign.</p>
            <Link to="/tools/funding-cost-calculator" className="block mt-6">
              <Button className="bg-white text-[#0A2540] hover:bg-[#0066FF] hover:text-white rounded-full h-11 w-full" data-testid="howit-calculator-cta">Run your own scenario</Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Repayment mechanics */}
      <section className="py-20 bg-[#F9FAFB] border-y border-[#E5E7EB]">
        <div className="mx-auto max-w-4xl px-6 lg:px-10">
          <div className="uppercase-label mb-3">Repayment</div>
          <h2 className="font-display text-3xl lg:text-4xl text-[#0A2540] font-light tracking-tight">How repayment actually works.</h2>
          <div className="mt-8 space-y-6 text-[#374151] leading-relaxed">
            <div>
              <div className="font-display font-semibold text-[#0A2540]">You never write us a check.</div>
              <p className="mt-2 text-sm">Every repayment flows through your attorney's trust account at settlement — the way any lien is satisfied.</p>
            </div>
            <div>
              <div className="font-display font-semibold text-[#0A2540]">Your attorney requests a payoff.</div>
              <p className="mt-2 text-sm">When the case settles, your attorney sends us a payoff request. We issue a statement showing the exact amount due based on the funding date and today's date.</p>
            </div>
            <div>
              <div className="font-display font-semibold text-[#0A2540]">The lien is satisfied from settlement proceeds.</div>
              <p className="mt-2 text-sm">Your attorney distributes the settlement: attorney fees, medical liens, our repayment, and finally the net to you. Everything is transparent and documented on the settlement statement you sign.</p>
            </div>
            <div>
              <div className="font-display font-semibold text-[#0A2540]">If the case loses or is dismissed, we absorb the loss.</div>
              <p className="mt-2 text-sm">No collections. No credit reporting. No pursuit. The advance was non-recourse from day one; a lost case simply ends the relationship. That's the risk we underwrite.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Attorney workflow */}
      <section className="py-20 bg-white">
        <div className="mx-auto max-w-4xl px-6 lg:px-10">
          <div className="uppercase-label mb-3">For attorneys</div>
          <h2 className="font-display text-3xl lg:text-4xl text-[#0A2540] font-light tracking-tight">What your attorney actually has to do.</h2>
          <p className="mt-4 text-[#374151] leading-relaxed">We're deliberate about respecting your attorney's time. The entire verification process is designed around a single 5–10 minute call.</p>
          <div className="mt-8 grid sm:grid-cols-2 gap-4">
            <div className="bg-[#F9FAFB] border border-[#E5E7EB] rounded-lg p-5">
              <div className="text-xs uppercase tracking-wider text-[#6B7280]">Step 1</div>
              <div className="font-display font-semibold text-[#0A2540] mt-2">Confirm representation</div>
              <p className="text-sm text-[#374151] mt-2">Verify the attorney-client relationship. Signed representation agreement is enough.</p>
            </div>
            <div className="bg-[#F9FAFB] border border-[#E5E7EB] rounded-lg p-5">
              <div className="text-xs uppercase tracking-wider text-[#6B7280]">Step 2</div>
              <div className="font-display font-semibold text-[#0A2540] mt-2">Send the case file</div>
              <p className="text-sm text-[#374151] mt-2">Crash report, medicals, demand letter or complaint, short case memo. Most attorneys have this ready in 30 minutes.</p>
            </div>
            <div className="bg-[#F9FAFB] border border-[#E5E7EB] rounded-lg p-5">
              <div className="text-xs uppercase tracking-wider text-[#6B7280]">Step 3</div>
              <div className="font-display font-semibold text-[#0A2540] mt-2">Acknowledge the lien</div>
              <p className="text-sm text-[#374151] mt-2">One-page acknowledgment stating the attorney will honor the funding lien at settlement. Standard document across the industry.</p>
            </div>
            <div className="bg-[#F9FAFB] border border-[#E5E7EB] rounded-lg p-5">
              <div className="text-xs uppercase tracking-wider text-[#6B7280]">Step 4</div>
              <div className="font-display font-semibold text-[#0A2540] mt-2">Request the payoff at settlement</div>
              <p className="text-sm text-[#374151] mt-2">At settlement, one email to us produces the payoff statement. Attorney satisfies from trust account and disburses the net to the plaintiff.</p>
            </div>
          </div>
          <div className="mt-8 text-sm">
            <Link to="/for-attorneys" className="text-[#0066FF] hover:underline font-medium">For attorneys → the full attorney workflow, client funding requests, and portfolio lending</Link>
          </div>
        </div>
      </section>

      {/* Value props */}
      <section className="py-20 bg-white">
        <div className="mx-auto max-w-7xl px-6 lg:px-10">
          <h2 className="font-display text-3xl lg:text-4xl text-[#0A2540] font-light tracking-tight">What makes Instabridge different</h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5 mt-10">
            {VALUE_PROPS.map((v) => (
              <div key={v.t} className="bg-white border border-[#E5E7EB] rounded-xl p-6">
                <CheckCircle2 className="w-5 h-5 text-[#0066FF]" />
                <h3 className="font-display text-lg font-semibold text-[#0A2540] mt-3">{v.t}</h3>
                <p className="text-sm text-[#6B7280] mt-2 leading-relaxed">{v.d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Case types */}
      <section className="py-20 bg-[#F9FAFB]">
        <div className="mx-auto max-w-7xl px-6 lg:px-10">
          <h2 className="font-display text-3xl lg:text-4xl text-[#0A2540] font-light tracking-tight">Cases we fund</h2>
          <p className="mt-3 text-[#6B7280] max-w-2xl">If you have an active personal-injury or civil case with an attorney, we can probably help. Common case types we fund:</p>
          <div className="mt-8 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
            {CASE_TYPES.map((c) => {
              const slug = CASE_TYPE_TO_SLUG[c];
              const commonClass = "bg-white border border-[#E5E7EB] rounded-lg px-4 py-3 text-sm text-[#374151]";
              if (slug) {
                return (
                  <Link key={c} to={`/accidents/${slug}`}
                        className={`${commonClass} hover:border-[#0066FF] hover:text-[#0066FF] transition-colors flex items-center justify-between`}
                        data-testid={`howit-case-link-${slug}`}>
                    <span>{c}</span>
                    <ArrowRight className="w-3.5 h-3.5" />
                  </Link>
                );
              }
              return (
                <div key={c} className={commonClass}>{c}</div>
              );
            })}
          </div>
          <p className="mt-5 text-sm text-[#6B7280]" data-testid="howit-other-case-note">
            <span className="text-[#0A2540] font-medium">Don&apos;t see your case type?</span> You can still apply — if you have an active claim with an attorney, we&apos;ll review it.
          </p>
        </div>
      </section>

      <section className="py-20 bg-white border-t border-[#E5E7EB]">
        <div className="mx-auto max-w-3xl px-6 lg:px-10 text-center">
          <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-[#0066FF]/10 border border-[#0066FF]/25 text-[#0052CC] text-xs font-semibold uppercase tracking-wider mb-5" data-testid="howit-final-reassurance">
            <ShieldCheck className="w-3.5 h-3.5" /> If you lose, you owe $0
          </div>
          <h2 className="font-display text-3xl lg:text-4xl text-[#0A2540] font-semibold">Ready to apply?</h2>
          <p className="mt-3 text-[#6B7280]">5 minutes. No obligation. Funding decisions in hours.</p>
          <Link to="/apply" className="inline-block mt-8">
            <Button className="btn-primary-navy rounded-full h-12 px-8" data-testid="howit-apply-btn">Start your application <ArrowRight className="w-4 h-4 ml-2" /></Button>
          </Link>
          <TrustCues variant="pill" audience="consumer" align="center" testid="howit-trust-pill" />
        </div>
      </section>

      <Footer />
    </div>
  );
}
